import React, { useEffect, useMemo, useState } from "react";
import { getCurrentTaxYear, prePopulateFilterTagFromUrl } from "../../../../../helpers/utils/Utils";
import ButtonTray from "../../../../../components/common/form/ButtonTray";
import { DataTable } from "../../../../../components/common/datatable/DataTable";
import Modal from "../../../../../components/common/form/Modal";
import { SearchInputWrapper } from "../../../../../components/common/datatable/filter/SearchInputWrapper";
import { DataTableColumn } from "../../../../../components/common/datatable/DataTableTypes";
import { useSearchAllJurisdictionQuery } from "../../../../data/jurisdiction/jurisdictionApi";
import {
    autoCalculateSummaryRow,
    calculateCurrentWithExemptionEstimateValue,
    calculateCurrentWoExemptAmountValue,
    getTaxRateYearValue, jsonCloneWithDates,
    sortSelectedJurisdiction,
    updateSummarySectionRow,
    yearArray
} from "../../../../../helpers/utils/OrderDetailUtils";
import CollectorSearchInputWrapper from "../../../../../components/common/datatable/filter/CollectorSearchInputWrapper";
import { useGetPrimaryCollectorByStateAndCountyQuery } from "../../../../data/appraisalRoll/appraisalRollsApi";
import { useGetCollectorByIdQuery } from "../../../../data/collector/collector";
import { skipToken } from "@reduxjs/toolkit/query";

interface Collector {
    id: string;
    name: string;
}

interface TaxRate {
    year: number;
    rate: number;
}

interface CertificateNote {
    sort: number;
    comment: string;
    bold?: boolean;
    yellowHighlight?: boolean;
    redColor?: boolean;
}

interface Jurisdiction {
    id: string;
    name: string;
    collector: Collector | null;
    taxRates?: TaxRate[];
    nonCollecting?: boolean;
    inActive?: boolean;
    certificateNotes: CertificateNote[];
}

interface JurisdictionListEntry {
    collectorName: string | null;
    jurisdictionName: string | null;
    jurisdictionId: string | null;
    collectorId: string | null;
    tax_rate: number | null;
    bill_year: number;
    currentMonthDue: number | null;
    nextMonthTaxDue: number | null;
    baseTax: number | null;
    baseDue: number | null;
    tax_rate_List: TaxRate[];
    taxDataSource: string;
    asOfDate: Date;
    placeHolder: boolean;
    nonCollecting?: boolean;
    inActive?: boolean;
    collectorAccountNumber: string;
}

interface Account {
    accountNotes: CertificateNote[];
    jurisdictionList: JurisdictionListEntry[];
    assessments?: any[];
}

interface Props {
    show: boolean;
    setShow: (value: boolean) => void;
    certificate: any;
    setCertificate: any;
    showAccount: number;
    county: any; // countyId for DataTable filter
    countyName?: string; // county name for primary-collector API (fallback only)
    state?: string; // state abbreviation for primary-collector API (fallback only)
    setUpdateTotalTaxValue: (val: boolean) => void;
    setIsSavedResult: (val: boolean) => void;
    orderId: string;
    setUpdatedAccountIndexList: any;
    setRender: (val: boolean) => void;
    primaryCollector?: any;
}

const JurisdictionPickerModal: React.FC<Props> = ({
    show,
    setShow,
    certificate,
    setCertificate,
    showAccount,
    county,
    countyName,
    state,
    setUpdateTotalTaxValue,
    setIsSavedResult,
    orderId,
    setUpdatedAccountIndexList,
    setRender,
    primaryCollector
}) => {

    const [selectedJurisdictionList, setSelectedJurisdictionList] = useState<Jurisdiction[]>([]);
    const [loading, setLoading] = useState(false);
    const [collectorValue, setCollectorValue] = useState<string | null>(null);
    const [startYear, setStartYear] = useState<any>(getCurrentTaxYear());
    const [endYear, setEndYear] = useState<any>(getCurrentTaxYear());
    const [selectedCollectorId, setSelectedCollectorId] = useState<string | null>(null);
    const [shouldFetchPrimary, setShouldFetchPrimary] = useState(false);
    const [primaryLookupParams, setPrimaryLookupParams] = useState<{stateAbbr?: string, countyName?: string}>({});

    const selectedCollectorQuery = useGetCollectorByIdQuery(selectedCollectorId ?? skipToken);

    const primaryCollectorQuery = useGetPrimaryCollectorByStateAndCountyQuery(
        shouldFetchPrimary && primaryLookupParams.stateAbbr && primaryLookupParams.countyName
            ? { stateAbbr: primaryLookupParams.stateAbbr, countyName: primaryLookupParams.countyName }
            : skipToken
    );

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        setLoading(true)
        const isValidYear =
            (startYear?.toString().length === 4 && endYear?.toString().length === 4) ||
            (startYear === undefined && endYear === undefined) ||
            (startYear?.toString().length === 4 && endYear === undefined) ||
            (startYear === undefined && endYear?.toString().length === 4);

        if (selectedJurisdictionList?.length > 0 && isValidYear) {
            const taxYears = yearArray(startYear, endYear);

            let updatedCertificate = jsonCloneWithDates(certificate);
            let account = { ...updatedCertificate.accounts[showAccount] };
            let updatedAccountNotes = [...(account.accountNotes ?? [])];
            let
             updatedJurisdictionList = [...(account.jurisdictionList ?? [])];

            for (const year of taxYears) {
                for (const cur of selectedJurisdictionList) {
                    if (cur?.certificateNotes) {
                        for (const curNote of cur.certificateNotes) {
                            if (!updatedAccountNotes.some(el => el.comment === curNote.comment)) {
                                updatedAccountNotes.push({
                                    sort: curNote.sort,
                                    comment: curNote.comment,
                                    bold: curNote.bold,
                                    orderId,
                                    saveAsCanned: false,
                                    yellowHighlight: curNote.yellowHighlight,
                                    redColor: curNote.redColor,
                                    relationalId: cur.id,
                                });
                            }
                        }
                    }

                    const jurEntry: JurisdictionListEntry = {
                        collectorName: cur?.collector?.name ?? null,
                        jurisdictionName: cur?.name ?? null,
                        jurisdictionId: cur?.id ?? null,
                        collectorId: cur?.collector?.id ?? null,
                        tax_rate: cur?.taxRates ? getTaxRateYearValue(cur.taxRates, year) : null,
                        bill_year: year,
                        currentMonthDue: cur?.nonCollecting || cur?.inActive ? 0.0 : null,
                        nextMonthTaxDue: cur?.nonCollecting || cur?.inActive ? 0.0 : null,
                        baseTax: cur?.nonCollecting || cur?.inActive ? 0.0 : null,
                        baseDue: cur?.nonCollecting || cur?.inActive ? 0.0 : null,
                        tax_rate_List: cur?.taxRates ?? [],
                        taxDataSource: 'EDITOR',
                        asOfDate: new Date(),
                        placeHolder: false,
                        nonCollecting: cur?.nonCollecting ?? false,
                        inActive: cur?.inActive ?? false,
                        collectorAccountNumber: '',
                    };

                    if (
                        updatedJurisdictionList.length === 1 &&
                        !updatedJurisdictionList[0]?.collectorName &&
                        !updatedJurisdictionList[0]?.jurisdictionName &&
                        !updatedJurisdictionList[0]?.jurisdictionId
                    ) {
                        updatedJurisdictionList[0] = jurEntry;
                    } else {
                        updatedJurisdictionList.push(jurEntry);
                    }
                    updatedCertificate.accounts[showAccount].jurisdictionList = updatedJurisdictionList;

                    const jurisdictionId = updatedJurisdictionList.length - 1;
                    updatedCertificate = updateSummarySectionRow(updatedCertificate, showAccount, jurisdictionId, year);
                    updatedCertificate = autoCalculateSummaryRow(updatedCertificate, showAccount);
                }
            }
            updatedCertificate.accounts[showAccount].accountNotes = updatedAccountNotes;
            setCertificate(updatedCertificate);

            // Compute exemption values using updated copy, collect results and merge
            calculateCurrentWoExemptAmountValue(
                updatedCertificate,
                showAccount,
                account?.assessments?.[0],
                setRender,
                setCertificate
            );

            calculateCurrentWoExemptAmountValue(
                updatedCertificate,
                showAccount,
                account?.assessments?.[1],
                setRender,
                setCertificate
            );

            calculateCurrentWithExemptionEstimateValue(
                updatedCertificate,
                showAccount,
                account?.assessments?.[0],
                setRender,
                setCertificate
            );

            calculateCurrentWithExemptionEstimateValue(
                updatedCertificate,
                showAccount,
                account?.assessments?.[1],
                setRender,
                setCertificate
            );

            setIsSavedResult(false);
            setUpdateTotalTaxValue(true);

            setUpdatedAccountIndexList((prev: any) =>
                prev.includes(showAccount) ? prev : [...prev, showAccount]
            );

            setShow(false);
            setLoading(false)
        } else {
            console.log('Invalid input parameters');
        }
    };

    const computeEffectiveTaxYear = (collector: any) => {
        const currentTaxYear = getCurrentTaxYear();
        if (collector && collector.billsReleased) {
            return currentTaxYear;
        }
        return currentTaxYear - 1;
    };

    const handleCollectorSelect = (collector: any) => {
        if (!collector) {
            setSelectedCollectorId(null);
            setShouldFetchPrimary(false);
            setPrimaryLookupParams({});
            const current = getCurrentTaxYear();
            setStartYear(current);
            setEndYear(current);
            return;
        }
        setSelectedCollectorId(collector.id);
        setPrimaryLookupParams({ stateAbbr: collector.stateAbbr, countyName: collector.countyName });
    };

    useEffect(() => {
        const col = selectedCollectorQuery.data as any;
        console.log("col===>  ", col)
        if (!col) return;
        if (col.isPrimaryCollector) {
            const y = computeEffectiveTaxYear(col);
            setStartYear(y);
            setEndYear(y);
            setShouldFetchPrimary(false);
        } else {
            if (!col.billsReleased) {
                setShouldFetchPrimary(true);
            } else {
                const y = computeEffectiveTaxYear(col);
                setStartYear(y);
                setEndYear(y);
                setShouldFetchPrimary(false);
            }
        }
    }, [selectedCollectorQuery.data]);

    useEffect(() => {
        const primary = primaryCollectorQuery.data as any;
        if (!primary) return;
        const y = computeEffectiveTaxYear(primary);
        setStartYear(y);
        setEndYear(y);
    }, [primaryCollectorQuery.data]);


    const columns: DataTableColumn[] = [
        {
            label: "",
            accessor: "id", canSort: false,
            cell: (props: any) => {
                return <>
                    <input
                        type="checkbox"
                        onChange={(e) => {
                            const isAlreadySelected = selectedJurisdictionList.some(
                                (item) => item.id === props.row.original.id
                            );
                            if (isAlreadySelected) {
                                setSelectedJurisdictionList((prev) =>
                                    prev.filter((item) => item.id !== props.row.original.id)
                                );
                            } else {
                                setSelectedJurisdictionList((prev) => [...prev, props.row.original]);
                            }
                        }}
                        onClick={() => {
                            setRender(props.row.original.id + Date.now());
                        }}
                        checked={selectedJurisdictionList.some(
                            (item) => item.id === props.row.original.id
                        )}
                    />

                </>

            }
        },
        {
            label: "Jurisdiction", accessor: "name",
            filterOptions: [{
                filterComponent: <SearchInputWrapper type={"text"} placeholder={"Enter Jurisdiction..."}
                    name={"name"} accessor={"name"} />,
            }]
        },

        {
            label: "Collector",
            accessor: "collector.name",
            filterOptions: [{
                filterComponent: <CollectorSearchInputWrapper value={collectorValue || ''} accessor="collectorName" onCollectorSelect={handleCollectorSelect} />,
                overrideFilterAccessor: "collectorName",
                overrideFilterLabel: "Collector",
            }]
        }
        ,

        {
            label: "Codes(s)", accessor: "jurisdictionCodes.code", canSort: false,
            cell: (props: any) => {
                return <>
                    {
                        props.row.original.jurisdictionCodes && props.row.original.jurisdictionCodes.map((jurisdiction: any, index: any) => {
                            return (
                                <>
                                    <span className="text_wrap_msa_grid"
                                        title={jurisdiction.type}>{jurisdiction.code}{index + 1 >= props.row.original.jurisdictionCodes.length ? '' : ', '}</span>
                                </>
                            )
                        })}
                </>
            },
            filterOptions: [{
                filterComponent: <SearchInputWrapper type={"text"} placeholder={"Enter Jurisdiction Code..."}
                    accessor={"jurisdictionCode"} />,
                overrideFilterLabel: "Code",
                overrideFilterAccessor: "code"
            }]
        },

    ]
    useMemo(() => {
        if (primaryCollector) {
            setCollectorValue(primaryCollector?.name);
        }
    }, []);

    useEffect(() => {
        if (primaryCollector) {
            const y = computeEffectiveTaxYear(primaryCollector);
            setStartYear(y);
            setEndYear(y);
        }
    }, [primaryCollector]);

    let customFilter: Record<string, any> = {
        countyId: county,
        type: "picker",
    };
    if (primaryCollector !== null) customFilter.collectorName = primaryCollector?.name;

    return (
        <>

            <Modal
                title={"Jurisdiction Selector"}
                show={show}
                setShow={setShow}
                className="w-2/4 bg-text-0 rounded-lg shadow-lg"
                fields={
                    <>
                        <div className="flex gap-3">
                            <div className="flex justify-between w-full">
                                <div className="" style={{ width: "inherit" }}><label>Start
                                    Year</label><input type="number" name="name"
                                        placeholder="Enter Start Year..."
                                        value={startYear}
                                        onChange={(e) => {
                                            e.preventDefault();
                                            let year = e.target.value.length > 4 ? e.target.value.slice(0, 4) : e.target.value;
                                            if (year !== null && year !== "" && endYear !== null && year.length === 4 && endYear !== "" && year > endYear && endYear !== undefined) {
                                                year = ""
                                            } else if (endYear === undefined && year > endYear) {
                                                year = ""
                                            }
                                            setStartYear(year);
                                        }}
                                        className="bg-text-0 border border-text-3 form_control p-2 placeholder:text-text-2 rounded-md text-surface-0 w-full"
                                        required={true} />
                                </div>
                            </div>
                            <div className=" flex justify-between w-full">
                                <div className="" style={{ width: "inherit" }}><label>End
                                    Year</label><input type="number" name="name"
                                        placeholder="Enter End Year..."
                                        value={endYear}
                                        onChange={(e) => {
                                            e.preventDefault();

                                            let year = e.target.value.length > 4 ? e.target.value.slice(0, 4) : e.target.value;
                                            if (year !== null && year !== "" && startYear !== undefined && year.length === 4 && startYear !== null && startYear !== "" && year < startYear) {
                                                year = ""
                                            } else if (startYear === undefined && endYear < startYear) {
                                                year = ""
                                            }
                                            setEndYear(year);
                                        }}
                                        className="bg-text-0 border border-text-3 form_control p-2 placeholder:text-text-2 rounded-md text-surface-0 w-full"
                                        required={true} />
                                </div>
                            </div>
                        </div>
                        <DataTable
                            columns={columns}
                            actions={{
                                fetchAction: useSearchAllJurisdictionQuery,
                            }}

                            defaultFilter={{
                                pageNumber: 0,
                                pageSize: 5,
                                ...customFilter

                            }}
                            renderOptions={{ skipUserSettingRender: true, skipFilterButtonRender: true }}
                            appendFilterToUrl={false}
                            defaultFilterFormExpanded={true}
                            userSettingGroup={""}
                            basePermission={"JURI"}
                        />
                        {selectedJurisdictionList.length > 0 && (
                            <>
                                <div className="mt-3">
                                    <h3 className="mb-2 text-2xl font-semibold">Selected Jurisdiction</h3>
                                    <table role="table" className="table table-hover table-striped w-full">
                                        <thead className="bg-highlight-5 text-text-0 relative text-left">
                                            <tr role="row">
                                                <th><span>Jurisdiction</span></th>
                                                <th>Collector</th>
                                            </tr>
                                        </thead>

                                        <tbody
                                            role="rowgroup"
                                            className={selectedJurisdictionList && selectedJurisdictionList.length > 4 ? "table_height" : undefined}
                                        >
                                            {sortSelectedJurisdiction(selectedJurisdictionList).map((current: any) => {
                                                return (
                                                    <tr role="row"
                                                        key={current.id}> {/* Added key for unique identification */}
                                                        <td role="cell">{current.name}</td>
                                                        <td role="cell">{current.collector != null ? current.collector.name : ''}</td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </>
                        )}
                        <div className="form-group row flex">
                            <div className="col-sm-4 ml-3">

                                <ButtonTray
                                    buttons={[
                                        {
                                            buttonProps: {
                                                btnText: "Cancel",
                                                onClick: () => setShow(false),
                                                type: "close",
                                            },
                                            buttonType: "button",
                                        },
                                        {
                                            buttonProps: {
                                                btnText: "Select",
                                                onClick: handleSubmit,
                                                type: "submit",
                                                isLoading: loading,
                                                isDisabled: selectedJurisdictionList.length <= 0
                                            },
                                            buttonType: "button",
                                        },
                                    ]}
                                    align="start"
                                    gap={2}
                                />
                            </div>
                            {(startYear.length < 4 || endYear.length < 4) &&
                                <>
                                    <div className="flex mt-3.5 col-sm-9 ml-48 items-end ">
                                        <i
                                            className={"fa fa-warning fa-2x text-highlight-5"}></i>
                                        <span
                                            className="text-error-1 ml-3">Invalid Start year or End year provided!</span>

                                    </div>
                                </>
                            }
                        </div>
                    </>
                }
            />
        </>
    )
}
export default JurisdictionPickerModal;